import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, Platform, TouchableOpacity } from 'react-native';
import { X, Share, PlusSquare, Download } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';

export const PWAInstallPrompt = () => {
    const { colors } = useTheme();
    const [showPrompt, setShowPrompt] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        // Only run on web
        if (Platform.OS !== 'web') return;

        // Check if already in standalone mode (PWA installed)
        const isInStandaloneMode = () => {
            return (
                window.matchMedia('(display-mode: standalone)').matches ||
                (window.navigator as any).standalone ||
                document.referrer.includes('android-app://')
            );
        };

        if (isInStandaloneMode()) return;

        // Check if iOS
        const userAgent = window.navigator.userAgent.toLowerCase();
        const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
        setIsIOS(isIOSDevice);

        // Handle Android/Chrome install prompt
        const handleBeforeInstallPrompt = (e: any) => {
            e.preventDefault();
            setDeferredPrompt(e);
            // Show prompt after a delay to not be intrusive immediately
            setTimeout(() => setShowPrompt(true), 3000);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // For iOS, show prompt if not standalone (simplest check)
        if (isIOSDevice) {
            setTimeout(() => setShowPrompt(true), 3000);
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstallClick = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                setShowPrompt(false);
            }
            setDeferredPrompt(null);
        }
    };

    const handleDismiss = () => {
        setShowPrompt(false);
    };

    if (!showPrompt) return null;

    return (
        <View style={styles.container}>
            <View style={[styles.content, { backgroundColor: colors.surface, borderColor: colors.border }]}>
                <TouchableOpacity style={styles.closeButton} onPress={handleDismiss}>
                    <X size={20} color={colors.textSecondary} />
                </TouchableOpacity>

                <View style={styles.header}>
                    <View style={[styles.iconContainer, { backgroundColor: colors.primary + '20' }]}>
                        <Download size={24} color={colors.primary} />
                    </View>
                    <View style={styles.textContainer}>
                        <Text style={[styles.title, { color: colors.text }]}>Instale o App</Text>
                        <Text style={[styles.description, { color: colors.textSecondary }]}>
                            Adicione à tela de início para uma experiência melhor e em tela cheia.
                        </Text>
                    </View>
                </View>

                {isIOS ? (
                    <View style={[styles.instructions, { backgroundColor: colors.background }]}>
                        <View style={styles.step}>
                            <Text style={[styles.stepText, { color: colors.text }]}>1. Toque em </Text>
                            <Share size={20} color="#007AFF" style={{ marginHorizontal: 4 }} />
                            <Text style={[styles.stepText, { color: colors.text }]}>Compartilhar</Text>
                        </View>
                        <View style={styles.step}>
                            <Text style={[styles.stepText, { color: colors.text }]}>2. Selecione </Text>
                            <PlusSquare size={20} color={colors.text} style={{ marginHorizontal: 4 }} />
                            <Text style={[styles.stepText, { color: colors.text }]}>Adicionar à Tela de Início</Text>
                        </View>
                    </View>
                ) : (
                    <TouchableOpacity
                        style={[styles.installButton, { backgroundColor: colors.primary }]}
                        onPress={handleInstallClick}
                        disabled={!deferredPrompt}
                    >
                        <Text style={styles.installButtonText}>Instalar Agora</Text>
                    </TouchableOpacity>
                )}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        position: 'absolute',
        bottom: 20, // Floating above bottom
        left: 20,
        right: 20,
        zIndex: 9999, // Ensure it's on top of everything
        maxWidth: 500,
        alignSelf: 'center', // Center on desktop
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 10,
    },
    content: {
        borderRadius: 16,
        padding: 20,
        borderWidth: 1,
    },
    closeButton: {
        position: 'absolute',
        top: 10,
        right: 10,
        padding: 5,
        zIndex: 1,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 16,
    },
    iconContainer: {
        width: 48,
        height: 48,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 16,
    },
    textContainer: {
        flex: 1,
    },
    title: {
        fontSize: 18,
        fontWeight: 'bold',
        marginBottom: 4,
    },
    description: {
        fontSize: 14,
        lineHeight: 20,
    },
    instructions: {
        marginTop: 8,
        padding: 12,
        borderRadius: 12,
    },
    step: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 8,
        flexWrap: 'wrap',
    },
    stepText: {
        fontSize: 14,
    },
    installButton: {
        paddingVertical: 12,
        borderRadius: 10,
        alignItems: 'center',
        marginTop: 8,
    },
    installButtonText: {
        color: '#fff',
        fontSize: 16,
        fontWeight: '600',
    },
});
